import MainLayout from "@/components/layout/MainLayout";
import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Calendar } from '@/components/ui/calendar';
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { getBranches } from '@/integrations/supabase/services';
import { getRentalsByBranchAndDate, getShiftsByBranchAndMonth, getShiftsByDateRange } from '@/integrations/supabase/report-services';
import { Branch, RentalWithRelations, ShiftWithRelations } from '@/types/models';
import { useAuth } from '@/context/AuthContext';
import * as XLSX from 'xlsx';
import { Calendar as CalendarIcon } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { startOfWeek, endOfWeek, format } from "date-fns";
import { es } from "date-fns/locale";
import { DateRange } from "react-day-picker";

const ReportsPage: React.FC = () => {
  const [branches, setBranches] = useState<Branch[]>([]);
  const [selectedBranch, setSelectedBranch] = useState<string>('');
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(new Date());
  const [reportType, setReportType] = useState<'daily' | 'weekly' | 'monthly'>('daily');
  const [selectedMonth, setSelectedMonth] = useState<string>((new Date().getMonth() + 1).toString());
  const [selectedYear, setSelectedYear] = useState<string>(new Date().getFullYear().toString());
  const [selectedWeek, setSelectedWeek] = useState<DateRange | undefined>();

  const { profile } = useAuth();

  useEffect(() => {
    const fetchBranches = async () => {
      const branchesData = await getBranches();
      setBranches(branchesData);
    };
    fetchBranches();
  }, []);

  useEffect(() => {
    if (profile?.role === 'branch_admin' && profile.branch_id) {
      setSelectedBranch(profile.branch_id);
    }
  }, [profile]);

  const generateDailyDetailExcel = (rentals: RentalWithRelations[], date_string: string, branchName: string) => {
    const wb = XLSX.utils.book_new();
    const wsData: (string | number | null)[][] = [
      ['Sucursal:', branchName],
      ['Operador:', profile?.full_name],
      [],
      ['Cliente', 'Plan', 'Inicio', 'Fin', 'Pagado']
    ];

    let totalAmount = 0;
    rentals.forEach(rental => {
      wsData.push([
        rental.customer?.name || '',
        rental.plan?.name || '',
        new Date(rental.start_date).toLocaleString(),
        rental.end_date ? new Date(rental.end_date).toLocaleString() : '',
        rental.total_amount || 0
      ]);
      totalAmount += rental.total_amount || 0;
    });

    wsData.push([]);
    wsData.push(['Total', '', '', '', totalAmount]);
    wsData.push(['Cantidad de Registros', '', '', '', rentals.length]);

    const ws = XLSX.utils.aoa_to_sheet(wsData);
    XLSX.utils.book_append_sheet(wb, ws, "Reporte Diario");
    XLSX.writeFile(wb, `reporte_diario_${branchName}_${date_string}.xlsx`);
  }

  const generateShiftSummaryExcel = (shifts: ShiftWithRelations[], date_string: string, branchName: string, reportType: 'weekly' | 'monthly') => {
    const wb = XLSX.utils.book_new();
    const sheetName = reportType === 'weekly' ? 'Reporte Semanal' : 'Reporte Mensual';
    const fileName = reportType === 'weekly' ? 'reporte_semanal' : 'reporte_mensual';

    const wsData: (string | number | null)[][] = [
      ['Sucursal:', branchName],
      ['Operador:', profile?.full_name],
      [],
      ['Día', 'Total Recaudado', 'Monto Operador', 'Monto Admin', 'Nro. Alquileres']
    ];

    let totalCollected = 0;
    let totalAdminAmount = 0;
    let totalOperatorAmount = 0;
    let totalRentals = 0;

    shifts.forEach(shift => {
      wsData.push([
        new Date(shift.start_time).toLocaleDateString(),
        shift.total_collected,
        shift.operator_amount,
        shift.amount_admin,
        shift.total_rentals || 0
      ]);

      totalCollected += shift.total_collected || 0;
      totalAdminAmount += shift.amount_admin || 0;
      totalOperatorAmount += shift.operator_amount || 0;
      totalRentals += shift.total_rentals || 0;
    });

    wsData.push([]);
    wsData.push(['Totales', totalCollected, totalOperatorAmount, totalAdminAmount, totalRentals]);

    const ws = XLSX.utils.aoa_to_sheet(wsData);
    XLSX.utils.book_append_sheet(wb, ws, sheetName);
    XLSX.writeFile(wb, `${fileName}_${branchName}_${date_string}.xlsx`);
  }

  const handleGenerateReport = async () => {
    if (!selectedBranch) return;

    const branchName = branches.find(b => b.id === selectedBranch)?.name || 'Desconocida';

    if (reportType === 'daily') {
      if (!selectedDate) return;
      const rentals = await getRentalsByBranchAndDate(selectedBranch, selectedDate);
      generateDailyDetailExcel(rentals, selectedDate.toISOString().split('T')[0], branchName);
    } else if (reportType === 'monthly') {
      if (!selectedMonth || !selectedYear) return;
      const shifts = await getShiftsByBranchAndMonth(selectedBranch, selectedMonth, selectedYear);
      generateShiftSummaryExcel(shifts, `${selectedYear}-${selectedMonth.padStart(2, '0')}`, branchName, 'monthly');
    } else if (reportType === 'weekly') {
      if (!selectedWeek || !selectedWeek.from || !selectedWeek.to) return;
      const shifts = await getShiftsByDateRange(selectedBranch, selectedWeek.from, selectedWeek.to);
      const date_string = `${selectedWeek.from.toISOString().split('T')[0]}_a_${selectedWeek.to.toISOString().split('T')[0]}`;
      generateShiftSummaryExcel(shifts, date_string, branchName, 'weekly');
    }
  };

  const years = Array.from({ length: 5 }, (_, i) => (new Date().getFullYear() - i).toString());
  const months = [
    { value: '1', label: 'Enero' }, { value: '2', label: 'Febrero' }, { value: '3', label: 'Marzo' },
    { value: '4', label: 'Abril' }, { value: '5', label: 'Mayo' }, { value: '6', label: 'Junio' },
    { value: '7', label: 'Julio' }, { value: '8', label: 'Agosto' }, { value: '9', label: 'Septiembre' },
    { value: '10', label: 'Octubre' }, { value: '11', label: 'Noviembre' }, { value: '12', label: 'Diciembre' }
  ];

  const handleWeekSelect = (date: Date | undefined) => {
    if (date) {
      setSelectedWeek({
        from: startOfWeek(date, { weekStartsOn: 0 }), // Sunday
        to: endOfWeek(date, { weekStartsOn: 0 }),     // Saturday
      });
    }
  };

  const isGenerateButtonDisabled = () => {
    if (!selectedBranch) return true;
    if (reportType === 'daily' && !selectedDate) return true;
    if (reportType === 'weekly' && !selectedWeek) return true;    
    if (reportType === 'monthly' && (!selectedMonth || !selectedYear)) return true;
    return false;
  };

  return (
    <MainLayout>
      <div className="p-4 md:p-8">
        <Card>
          <CardHeader>
            <CardTitle>Generar Reporte</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>Tipo de Reporte</Label>
              <RadioGroup
                value={reportType}
                onValueChange={(value) => setReportType(value as 'daily' | 'monthly' | 'weekly')}
                className="flex space-x-4"
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="daily" id="daily" />
                  <Label htmlFor="daily">Diario</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="weekly" id="weekly" />
                  <Label htmlFor="weekly">Semanal</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="monthly" id="monthly" />
                  <Label htmlFor="monthly">Mensual</Label>
                </div>
              </RadioGroup>
            </div>

            <div className="flex flex-col md:flex-row md:items-start md:space-x-4 space-y-4 md:space-y-0">
              <div className="w-full md:w-1/3 space-y-2">
                <Label htmlFor="branch-select">Sucursal</Label>
                {profile?.role === 'branch_admin' ? (
                  <div className="flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50">
                    {branches.find(b => b.id === profile.branch_id)?.name || 'Cargando...'}
                  </div>
                ) : (
                  <Select onValueChange={setSelectedBranch} value={selectedBranch}>
                    <SelectTrigger id="branch-select">
                      <SelectValue placeholder="Seleccione una sucursal" />
                    </SelectTrigger>
                    <SelectContent>
                      {branches.map((branch) => (
                        <SelectItem key={branch.id} value={branch.id}>
                          {branch.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>

              <div className="w-full md:w-1/3 space-y-2">
                {reportType === 'daily' && (
                  <div className="w-1/2 space-y-2">
                    <Label>Día</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="outline" className="md:w-auto">
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {format(selectedDate, "PPP", { locale: es })}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0">
                        <Calendar
                          mode="single"
                          selected={selectedDate}
                          onSelect={setSelectedDate}
                          initialFocus
                          locale={es}
                          weekStartsOn={0}
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                )}
                {reportType === 'monthly' && (
                  <div className="flex space-x-4">
                    <div className="w-1/2 space-y-2">
                      <Label htmlFor="month-select">Mes</Label>
                      <Select onValueChange={setSelectedMonth} value={selectedMonth}>
                        <SelectTrigger id="month-select">
                          <SelectValue placeholder="Seleccione un mes" />
                        </SelectTrigger>
                        <SelectContent>
                          {months.map(month => (
                            <SelectItem key={month.value} value={month.value}>{month.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="w-1/2 space-y-2">
                      <Label htmlFor="year-select">Año</Label>
                      <Select onValueChange={setSelectedYear} value={selectedYear}>
                        <SelectTrigger id="year-select">
                          <SelectValue placeholder="Seleccione un año" />
                        </SelectTrigger>
                        <SelectContent>
                          {years.map(year => (
                            <SelectItem key={year} value={year}>{year}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                )}
                {reportType === 'weekly' && (
                  <div className="w-1/2 space-y-2">
                    <Label>Semana</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="outline" className="md:w-auto">
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {selectedWeek?.from ? (
                            <>
                              {format(selectedWeek.from, 'd', { locale: es })} -{' '}
                              {format(selectedWeek.to, 'd MMM yyyy', { locale: es })}
                            </>
                          ) : (
                            'Seleccionar semana'
                          )}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0">
                        <Calendar
                          mode="range"
                          selected={selectedWeek}
                          onDayClick={handleWeekSelect}
                          initialFocus
                          locale={es}
                          weekStartsOn={0}
                          className="rounded-md border"
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                )}
              </div>
            </div>
            <Button onClick={handleGenerateReport} disabled={isGenerateButtonDisabled()}>
              Generar Reporte
            </Button>
          </CardContent>
        </Card>
      </div>
    </MainLayout>
  );
};

export default ReportsPage;
